/**
 * 来客数 手入力画面 (v0.3 S-6)
 * 設計: docs/design/v03-visitor-count-generalization.md §5-2
 *
 * 既存の ManualEntryPage (labor/opex/delivery_sales、店舗×月の全置換方式) とは異なり、
 * visitor_counts は SCD-2 (1日単位の訂正履歴) のため、専用の CRUD API (visitor-counts.ts) を
 * 直接呼ぶ形で実装する。一覧テーブルは date/total_count/source/属性/remarks の5列に絞り、
 * 性別・年代等の属性入力はモーダルの「詳細」トグルで折りたたむ (Design レビュー H-1 対応)。
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Pencil, Trash2, ChevronDown, ChevronUp, AlertTriangle, Upload } from 'lucide-react';
import { MonthPicker } from '@/components/MonthPicker';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Modal, ConfirmDialog } from '@/components/Modal';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };

type VisitorCountRow = {
  id: string;
  store_node_id: string;
  visit_date: string;
  source_type: 'manual' | 'csv' | 'pos' | 'ticket_machine';
  total_count: number;
  male_count: number | null;
  female_count: number | null;
  unknown_gender_count: number | null;
  age_under20_count: number | null;
  age_20s_count: number | null;
  age_30s_count: number | null;
  age_40s_count: number | null;
  age_50s_count: number | null;
  age_60plus_count: number | null;
  new_customer_count: number | null;
  repeat_customer_count: number | null;
  remarks: string | null;
};

const SOURCE_LABELS: Record<string, string> = {
  manual: '手入力', csv: 'CSV取込', pos: 'POS連携', ticket_machine: '食券機連携',
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthRange = (yearMonth: string): { from: string; to: string } => {
  const [y, m] = yearMonth.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  return { from: `${yearMonth}-01`, to: `${yearMonth}-${String(lastDay).padStart(2, '0')}` };
};

type FormState = {
  id: string | null;
  visit_date: string;
  total_count: string;
  male_count: string;
  female_count: string;
  unknown_gender_count: string;
  age_under20_count: string;
  age_20s_count: string;
  age_30s_count: string;
  age_40s_count: string;
  age_50s_count: string;
  age_60plus_count: string;
  new_customer_count: string;
  repeat_customer_count: string;
  remarks: string;
};

const emptyForm = (): FormState => ({
  id: null, visit_date: '', total_count: '',
  male_count: '', female_count: '', unknown_gender_count: '',
  age_under20_count: '', age_20s_count: '', age_30s_count: '', age_40s_count: '', age_50s_count: '', age_60plus_count: '',
  new_customer_count: '', repeat_customer_count: '', remarks: '',
});

const toBody = (f: FormState) => {
  const n = (v: string) => (v.trim() === '' ? undefined : Number(v));
  return {
    visit_date: f.visit_date,
    total_count: Number(f.total_count),
    male_count: n(f.male_count), female_count: n(f.female_count), unknown_gender_count: n(f.unknown_gender_count),
    age_under20_count: n(f.age_under20_count), age_20s_count: n(f.age_20s_count), age_30s_count: n(f.age_30s_count),
    age_40s_count: n(f.age_40s_count), age_50s_count: n(f.age_50s_count), age_60plus_count: n(f.age_60plus_count),
    new_customer_count: n(f.new_customer_count), repeat_customer_count: n(f.repeat_customer_count),
    remarks: f.remarks.trim() === '' ? undefined : f.remarks,
  };
};

export function VisitorCountsPage() {
  const qc = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [month, setMonth] = useState(currentYearMonth());
  const [modalOpen, setModalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<VisitorCountRow | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  const { data: stores = [] } = useQuery<Store[]>({ queryKey: ['stores'], queryFn: () => apiFetch<Store[]>('/stores') });

  const { from, to } = monthRange(month);
  const listQuery = useQuery<VisitorCountRow[]>({
    queryKey: ['visitor-counts', storeId, from, to],
    queryFn: () => apiFetch<VisitorCountRow[]>(`/visitor-counts?store_node_id=${storeId}&from=${from}&to=${to}`),
    enabled: Boolean(storeId),
  });

  const saveMut = useMutation({
    mutationFn: (f: FormState) => f.id
      ? apiFetch(`/visitor-counts/${f.id}`, { method: 'PUT', body: JSON.stringify(toBody(f)) })
      : apiFetch('/visitor-counts', { method: 'POST', body: JSON.stringify({ store_node_id: storeId, ...toBody(f) }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitor-counts', storeId, from, to] });
      toast.success('保存しました');
      setModalOpen(false);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/visitor-counts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitor-counts', storeId, from, to] });
      toast.success('削除しました');
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const handleCsvSelected = async (file: File) => {
    setCsvUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/visitor-counts/import`, {
        method: 'POST', body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        handleUnauthorized(res.status);
        throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? '取込に失敗しました');
      }
      const result = body.data as { imported: number; errors: { row: number; column?: string; message: string }[] };
      if (result.errors.length > 0) {
        toast.error(`${result.imported} 件取込・${result.errors.length} 件エラー (行${result.errors[0]!.row}: ${result.errors[0]!.message}${result.errors.length > 1 ? ' 他' : ''})`);
      } else {
        toast.success(`${result.imported} 件取込みました`);
      }
      qc.invalidateQueries({ queryKey: ['visitor-counts', storeId, from, to] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : 'CSV取込に失敗しました');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const openCreate = () => { setForm(emptyForm()); setShowDetails(false); setModalOpen(true); };
  const openEdit = (row: VisitorCountRow) => {
    setForm({
      id: row.id, visit_date: row.visit_date, total_count: String(row.total_count),
      male_count: row.male_count?.toString() ?? '', female_count: row.female_count?.toString() ?? '',
      unknown_gender_count: row.unknown_gender_count?.toString() ?? '',
      age_under20_count: row.age_under20_count?.toString() ?? '', age_20s_count: row.age_20s_count?.toString() ?? '',
      age_30s_count: row.age_30s_count?.toString() ?? '', age_40s_count: row.age_40s_count?.toString() ?? '',
      age_50s_count: row.age_50s_count?.toString() ?? '', age_60plus_count: row.age_60plus_count?.toString() ?? '',
      new_customer_count: row.new_customer_count?.toString() ?? '', repeat_customer_count: row.repeat_customer_count?.toString() ?? '',
      remarks: row.remarks ?? '',
    });
    setShowDetails(false);
    setModalOpen(true);
  };

  const rows = listQuery.data ?? [];
  const canSave = form.visit_date.trim() !== '' && form.total_count.trim() !== '' && !Number.isNaN(Number(form.total_count));

  const detailField = (label: string, key: Exclude<keyof FormState, 'id'>) => (
    <div>
      <label className="block text-xs text-neutral-sub mb-1">{label}</label>
      <input
        type="number" min={0} value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        className="w-full appearance-none px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Users size={26} className="text-brand-red" strokeWidth={1.75} />
          来客数 手入力
        </h1>
        <p className="text-sm text-neutral-sub mt-1">店舗・対象月ごとの来客数を日次で入力します。性別・年代等の属性は任意です。</p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="vc-store" className="block text-xs text-neutral-sub mb-1">店舗 *</label>
          <select id="vc-store" value={storeId} onChange={(e) => setStoreId(e.target.value)}
            className="min-w-[180px] px-3 py-2 border border-neutral-line rounded text-sm focus-ring">
            <option value="">選択してください</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.store_name}{s.status === 'closed' ? '（停止中）' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="vc-month" className="block text-xs text-neutral-sub mb-1">対象月 *</label>
          <MonthPicker id="vc-month" aria-label="対象月 (必須)" value={month} onChange={setMonth}
            className="px-3 py-2 border border-neutral-line rounded text-sm focus-ring" />
        </div>
        {storeId && (
          <div className="flex gap-2">
            <button type="button" onClick={openCreate}
              className="flex items-center gap-1 px-4 py-2 rounded bg-brand-red text-white text-sm font-medium hover:bg-brand-red-hover">
              <Plus size={14} /> 新規追加
            </button>
            <button type="button" disabled={csvUploading} onClick={() => csvInputRef.current?.click()}
              className="flex items-center gap-1 px-4 py-2 rounded border border-neutral-line text-sm font-medium hover:bg-surface-sunken disabled:opacity-50">
              <Upload size={14} /> {csvUploading ? '取込中…' : 'CSV取込'}
            </button>
            <input
              ref={csvInputRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsvSelected(f); }}
            />
          </div>
        )}
      </div>
      {storeId && (
        <p className="text-xs text-neutral-sub -mt-4">
          CSVフォーマット: <code className="font-mono bg-surface-sunken px-1.5 py-0.5 rounded text-[11px]">store_code, visit_date, total_count, remarks</code> (最小4列。属性列は今後拡張予定)
        </p>
      )}

      {storeId ? (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th scope="col" className="text-left px-3 py-2 whitespace-nowrap">日付</th>
                <th scope="col" className="text-right px-3 py-2 whitespace-nowrap">来客数</th>
                <th scope="col" className="text-left px-3 py-2 whitespace-nowrap">ソース</th>
                <th scope="col" className="text-left px-3 py-2 whitespace-nowrap">備考</th>
                <th scope="col" className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr><td colSpan={5} className="text-center text-neutral-sub py-6">読み込み中…</td></tr>
              )}
              {!listQuery.isLoading && rows.length === 0 && (
                <tr><td colSpan={5} className="text-center text-neutral-sub py-6">この月のデータはまだありません</td></tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-neutral-line">
                  <td className="px-3 py-2 whitespace-nowrap">{row.visit_date}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.total_count.toLocaleString('ja-JP')}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block text-[11px] px-1.5 py-0.5 rounded-full bg-surface-sunken text-neutral-sub">
                      {SOURCE_LABELS[row.source_type] ?? row.source_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-neutral-sub truncate max-w-[200px]">{row.remarks ?? ''}</td>
                  <td className="px-3 py-2">
                    {row.source_type === 'manual' && (
                      <div className="flex gap-1 justify-end">
                        <button type="button" onClick={() => openEdit(row)} aria-label="訂正"
                          className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1 flex items-center justify-center text-neutral-sub hover:text-brand-red">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(row)} aria-label="取消"
                          className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1 flex items-center justify-center text-neutral-sub hover:text-danger-text">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          店舗を選択すると入力欄が表示されます
        </div>
      )}

      <Modal
        open={modalOpen}
        title={form.id ? '来客数を訂正' : '来客数を追加'}
        onClose={() => setModalOpen(false)}
        size="md"
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded border border-neutral-line text-sm">
              キャンセル
            </button>
            <button type="button" disabled={!canSave || saveMut.isPending}
              onClick={() => saveMut.mutate(form)}
              className="px-4 py-2 rounded bg-brand-red text-white text-sm font-medium hover:bg-brand-red-hover disabled:opacity-50">
              {saveMut.isPending ? '保存中…' : '保存する'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-sub mb-1">日付 *</label>
              <input type="date" value={form.visit_date} disabled={!!form.id}
                onChange={(e) => setForm((prev) => ({ ...prev, visit_date: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring disabled:opacity-50" />
            </div>
            <div>
              <label className="block text-xs text-neutral-sub mb-1">来客数 (総計) *</label>
              <input type="number" min={0} value={form.total_count}
                onChange={(e) => setForm((prev) => ({ ...prev, total_count: e.target.value }))}
                className="w-full appearance-none px-3 py-2 border border-neutral-line rounded text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
            </div>
          </div>

          <button type="button" onClick={() => setShowDetails((v) => !v)}
            className="flex items-center gap-1 text-sm text-brand-red hover:underline">
            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            詳細を{showDetails ? '閉じる' : '入力する'} (性別・年代・新規/リピート、任意)
          </button>

          {showDetails && (
            <div className="space-y-3 p-3 bg-surface-sunken rounded">
              <div>
                <p className="text-xs font-semibold text-neutral-sub mb-1.5">性別</p>
                <div className="grid grid-cols-3 gap-2">
                  {detailField('男性', 'male_count')}
                  {detailField('女性', 'female_count')}
                  {detailField('不明', 'unknown_gender_count')}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-neutral-sub mb-1.5">年代</p>
                <div className="grid grid-cols-3 gap-2">
                  {detailField('〜19歳', 'age_under20_count')}
                  {detailField('20代', 'age_20s_count')}
                  {detailField('30代', 'age_30s_count')}
                  {detailField('40代', 'age_40s_count')}
                  {detailField('50代', 'age_50s_count')}
                  {detailField('60歳〜', 'age_60plus_count')}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-neutral-sub mb-1.5">新規/リピート</p>
                <div className="grid grid-cols-2 gap-2">
                  {detailField('新規', 'new_customer_count')}
                  {detailField('リピート', 'repeat_customer_count')}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-neutral-sub mb-1">備考</label>
            <input type="text" value={form.remarks} maxLength={500}
              onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
              className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring" />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="来客数データを取り消しますか？"
        destructive
        confirmLabel="取り消す"
        loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
        message={
          <div className="space-y-2">
            <p>{deleteTarget?.visit_date} の来客数データ ({deleteTarget?.total_count.toLocaleString('ja-JP')}人) を取り消します。</p>
            <div className="p-2 rounded bg-alert-bg border border-alert-border/40 text-xs text-alert-text flex items-start gap-1.5">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              この操作は履歴として記録され、監査ログから確認できます。
            </div>
          </div>
        }
      />
    </div>
  );
}
